import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ICartState } from "@/app_redux/reducers/slices/auth/cart_slice";
import { HiMiniClock } from "react-icons/hi2";
import { MdCurrencyRupee } from "react-icons/md";
import { PiVaultFill, PiWalletFill } from "react-icons/pi";

const SkeletonCardComponent = () => {
  return (
    <div className="flex flex-wrap my-5 gap-3">
      {[...Array(5)].map((_, index) => (
        <div
          key={index}
          className="h-auto w-full sm:w-[48%] lg:w-72 bg-gray-100 dark:bg-gray-200 p-4 rounded-lg animate-pulse"
        >
          <div className="h-4 bg-gray-200 dark:bg-gray-300 rounded w-1/2 mb-2"></div>
          <div className="h-8 bg-gray-200 dark:bg-gray-300 rounded w-1/3"></div>
        </div>
      ))}
    </div>
  );
};

const Card = ({ textColor, icon, label, value, bgColor }) => (
  <div className="h-[65px] w-full p-2 flex items-center gap-3 border-l first:border-l-0">

    <div className={`h-10 w-10 md:h-10 md:w-10 ${bgColor} flex items-center justify-center rounded-2xl`}>
      {icon}
    </div>

    <div className="leading-tight">
      <h2 className={`text-[10px] md:text-xs ${textColor} font-semibold`}>
        {label}
      </h2>

      <h2 className="text-sm md:text-base text-[#1C242F] font-medium">
        {value}
      </h2>
    </div>
  </div>
);

const CartHeaderComponent = () => {
  const isCartWalletDetailsLoading = useLoader("isCustomerWalletDetailsInProgress");
  const { getRouteKey } = useRouter();
  const { walletDetails } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState.state as ICartState;

  const showReserveWallet = walletDetails?.reserve_wallet_amount != null;

  return (
    <>
      {isCartWalletDetailsLoading ? (
        <SkeletonCardComponent />
      ) : (
        <div
          className={`grid grid-cols-2 md:grid-cols-3 ${
            showReserveWallet ? "lg:grid-cols-6" : "lg:grid-cols-5"
          } gap-3 py-2 border rounded-lg my-3`}
        >

          <Card
            textColor="text-[#FF3939]"
            bgColor="bg-[#FFF2F5]"
            icon={
              <span className="text-[#FF3939] text-sm sm:text-base">
                <HiMiniClock className="w-4 h-4 sm:w-5 sm:h-5" />
              </span>
            }
            label="Overdue Amount"
            value={walletDetails?.overdue_amount || 0}
          />

          <Card
            textColor="text-[#808DA0]"
            bgColor="bg-[#DCFFF3]"
            icon={
              <span className="text-green-500 text-sm sm:text-base">
                <MdCurrencyRupee className="w-4 h-4 sm:w-5 sm:h-5" />
              </span>
            }
            label="Wallet Balance"
            value={walletDetails?.wallet_amount || 0}
          />

          <Card
            textColor="text-[#808DA0]"
            bgColor="bg-[#FFF2F5]"
            icon={
              <span className="text-orange-500">
                <img
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  src="https://vetic-img.s3.ap-south-1.amazonaws.com/website/Website-Astro/icons/Marketing+Credit.png"
                />
              </span>
            }
            label="Marketing Credit"
            value={walletDetails?.wallets?.marketing_credit || 0}
          />

          <Card
            textColor="text-[#808DA0]"
            bgColor="bg-[#F3F0FF]"
            icon={
              <span className="text-purple-500 text-sm sm:text-base">
                <PiWalletFill className="w-4 h-4 sm:w-5 sm:h-5" />
              </span>
            }
            label="Advance Amount"
            value={walletDetails?.wallets?.advance_collection || 0}
          />

          <Card
            textColor="text-[#808DA0]"
            bgColor="bg-[#FFF6BE]"
            icon={
              <span className="text-yellow-500">
                <img
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  src="https://vetic-img.s3.ap-south-1.amazonaws.com/website/Website-Astro/icons/Paper.png"
                />
              </span>
            }
            label="Return/Refund Credit"
            value={walletDetails?.wallets?.credit_due_to_return || 0}
          />

          {showReserveWallet && (
            <Card
              textColor="text-[#808DA0]"
              bgColor="bg-[#E8EEFF]"
              icon={
                <span className="text-blue-500 text-sm sm:text-base">
                  <PiVaultFill className="w-4 h-4 sm:w-5 sm:h-5" />
                </span>
              }
              label="Reserve Wallet"
              value={walletDetails?.reserve_wallet_amount}
            />
          )}

        </div>
      )}
    </>
  );
};

export default CartHeaderComponent;
